import { Directive, ElementRef, NgZone, OnDestroy, inject, input, numberAttribute, output } from '@angular/core';

const EDGE_SIZE_PX = 6;

export type EdgeResizeDirection = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';

export interface EdgeResizeSize {
  width: number;
  height: number;
}

export interface EdgeResizeEvent extends EdgeResizeSize {
  direction: EdgeResizeDirection;
}

interface ResizeState {
  direction: EdgeResizeDirection;
  pointerId: number;
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
}

@Directive({
  selector: '[appEdgeResize]',
  exportAs: 'appEdgeResize',
  standalone: true,
})
export class EdgeResizeDirective implements OnDestroy {
  readonly #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #ngZone = inject(NgZone);

  #resizeState: ResizeState | null = null;
  #animationFrame: number | null = null;
  #pendingSize: EdgeResizeEvent | null = null;
  #cleanupResize: (() => void) | null = null;
  #restoreCursor: (() => void) | null = null;

  public readonly minWidth = input(300, { transform: numberAttribute });
  public readonly minHeight = input(400, { transform: numberAttribute });
  public readonly sizeChange = output<EdgeResizeEvent>();

  constructor() {
    this.#ngZone.runOutsideAngular(() => {
      const element = this.#elementRef.nativeElement;
      element.addEventListener('pointermove', this.#onPointerMove);
      element.addEventListener('pointerleave', this.#onPointerLeave);
      element.addEventListener('pointerdown', this.#onPointerDown);
    });
  }

  public ngOnDestroy(): void {
    const element = this.#elementRef.nativeElement;
    element.removeEventListener('pointermove', this.#onPointerMove);
    element.removeEventListener('pointerleave', this.#onPointerLeave);
    element.removeEventListener('pointerdown', this.#onPointerDown);
    this.#endResize();
  }

  public reset(): void {
    this.#endResize();
    const element = this.#elementRef.nativeElement;
    element.style.removeProperty('width');
    element.style.removeProperty('height');
  }

  readonly #onPointerMove = (event: PointerEvent): void => {
    if (!this.#resizeState) {
      this.#elementRef.nativeElement.style.cursor = this.#getCursor(this.#getResizeDirection(event));
      return;
    }

    if (event.pointerId !== this.#resizeState.pointerId) return;

    this.#pendingSize = this.#getResizedSize(event);
    this.#scheduleResize();
  };

  readonly #onPointerLeave = (): void => {
    if (!this.#resizeState) this.#elementRef.nativeElement.style.removeProperty('cursor');
  };

  readonly #onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;

    const direction = this.#getResizeDirection(event);
    if (!direction) return;

    event.preventDefault();
    const rect = this.#elementRef.nativeElement.getBoundingClientRect();
    this.#resizeState = {
      direction,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
    };

    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = this.#getCursor(direction);
    this.#restoreCursor = () => {
      document.body.style.cursor = previousCursor;
      this.#restoreCursor = null;
    };

    const onPointerUp = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== event.pointerId) return;
      this.#flushResize();
      this.#endResize();
    };

    const onPointerCancel = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== event.pointerId) return;
      this.#endResize();
    };

    document.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
    this.#cleanupResize = () => {
      document.removeEventListener('pointermove', this.#onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerCancel);
      this.#cleanupResize = null;
    };
  };

  #getResizeDirection(event: PointerEvent): EdgeResizeDirection | null {
    const rect = this.#elementRef.nativeElement.getBoundingClientRect();
    const nearTop = event.clientY - rect.top <= EDGE_SIZE_PX;
    const nearBottom = rect.bottom - event.clientY <= EDGE_SIZE_PX;
    const nearLeft = event.clientX - rect.left <= EDGE_SIZE_PX;
    const nearRight = rect.right - event.clientX <= EDGE_SIZE_PX;

    if (nearTop && nearLeft) return 'nw';
    if (nearTop && nearRight) return 'ne';
    if (nearBottom && nearLeft) return 'sw';
    if (nearBottom && nearRight) return 'se';
    if (nearTop) return 'n';
    if (nearBottom) return 's';
    if (nearLeft) return 'w';
    if (nearRight) return 'e';
    return null;
  }

  #getCursor(direction: EdgeResizeDirection | null): string {
    switch (direction) {
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'nw':
      case 'se':
        return 'nwse-resize';
      default:
        return '';
    }
  }

  #getResizedSize(event: PointerEvent): EdgeResizeEvent {
    const resizeState = this.#resizeState!;
    const deltaX = event.clientX - resizeState.startX;
    const deltaY = event.clientY - resizeState.startY;
    const widthDelta = resizeState.direction.includes('w') ? -deltaX : resizeState.direction.includes('e') ? deltaX : 0;
    const heightDelta = resizeState.direction.includes('n') ? -deltaY : resizeState.direction.includes('s') ? deltaY : 0;

    return {
      width: Math.max(this.minWidth(), resizeState.startWidth + widthDelta),
      height: Math.max(this.minHeight(), resizeState.startHeight + heightDelta),
      direction: resizeState.direction,
    };
  }

  #scheduleResize(): void {
    if (this.#animationFrame !== null) return;
    this.#animationFrame = requestAnimationFrame(() => this.#flushResize());
  }

  #flushResize(): void {
    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }

    const pendingSize = this.#pendingSize;
    this.#pendingSize = null;
    if (!pendingSize) return;

    const element = this.#elementRef.nativeElement;
    element.style.width = `${pendingSize.width}px`;
    element.style.height = `${pendingSize.height}px`;
    const { width, height } = element.getBoundingClientRect();
    this.#ngZone.run(() => this.sizeChange.emit({ width, height, direction: pendingSize.direction }));
  }

  #endResize(): void {
    this.#cleanupResize?.();
    this.#restoreCursor?.();
    this.#resizeState = null;
    this.#pendingSize = null;

    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }
}
